﻿import { FilterGroup } from '@fe/core';
import { NavDataFilter } from '@fe/flight-layer';
import { FeMap, FeMapPlugin, GridLayer } from '@fe/maplibre-extensions';
import * as turf from '@turf/turf';
import { Feature, FeatureCollection, GeoJsonProperties, MultiPolygon, Point, Polygon } from 'geojson';
import { ExpressionFilterSpecification, GeoJSONFeature, GeoJSONSource, Map as MaplibreMap, MapMouseEvent, PointLike } from 'maplibre-gl';
import { setNavDataWarning } from '../app/state/slices/workspace-slice';
import { store } from '../app/state/store';
import { UnitFormatter } from '../formatters';
import { NavDataContextMenuContent } from '../services/map-interaction-service';
import { FilterGroupType } from '../types/filter-group';
import { UDViewAirportSettingsModel, UDViewStaticDataModel, UDViewTerrainShadingModel, UDViewTerrainShadingType } from '../udView/udview';
import { RunwayPlugin } from './runway-plugin';

export const RANGE_RINGS_MIN_ZOOM = 3;
const HOVERABLE_LAYERS = ['navaid'];
const CONTEXT_MENU_LAYERS = [
    'airspace_border',
    'airspace_border2',
    'airspace_fill',
    'airspace_fill',
    'airspace_label',
    'airspace_label2',
    'sua_border',
    'sua_band',
    'sua_label'
];

//todo move to plugins folder
export class NavDataPlugin implements FeMapPlugin {
    private map: MaplibreMap | null = null;
    private navDataFilter = new NavDataFilter();
    private gridLayer = new GridLayer();
    private runways = new RunwayPlugin();
    private filterGroups: Partial<Record<FilterGroupType, FilterGroup[]>> = {};

    constructor(private readonly workspaceId: number) {}

    onAdd = (map: FeMap) => {
        this.map = map;

        this.navDataFilter.onAdd(map);
        this.gridLayer.onAdd(map);
        this.runways.onAdd(map);

        if (!map.getSource('fe-airport-range-rings-source')) {
            map.addSource('fe-airport-range-rings-source', {
                type: 'geojson',
                data: { type: 'FeatureCollection', features: [] }
            });
        }

        if (!map.getLayer('fe-airport-range-rings')) {
            map.addLayer(
                {
                    id: 'fe-airport-range-rings',
                    type: 'line',
                    source: 'fe-airport-range-rings-source',
                    minzoom: RANGE_RINGS_MIN_ZOOM,
                    layout: { visibility: 'none' },
                    paint: { 'line-width': 2, 'line-opacity': 0 }
                },
                'fe-airport-range-rings-slot'
            );
        }
        if (!map.getLayer('fe-airport-range-rings-fill')) {
            map.addLayer(
                {
                    id: 'fe-airport-range-rings-fill',
                    type: 'fill',
                    source: 'fe-airport-range-rings-source',
                    minzoom: RANGE_RINGS_MIN_ZOOM,
                    layout: {
                        visibility: 'none'
                    },
                    paint: {
                        'fill-opacity': 0
                    }
                },
                'fe-airport-range-rings-slot'
            );
        }
    };

    showGrid = () => {
        this.gridLayer.show();
    };

    hideGrid = () => {
        this.gridLayer.hide();
    };

    onRemove = (map: FeMap) => {
        this.navDataFilter.onRemove(map);
        this.gridLayer.onRemove();
        this.runways.onRemove();

        if (this.map?.getLayer('fe-airport-range-rings')) {
            this.map.removeLayer('fe-airport-range-rings');
        }
        if (this.map?.getLayer('fe-airport-range-rings-fill')) {
            this.map.removeLayer('fe-airport-range-rings-fill');
        }
        if (this.map?.getSource('fe-airport-range-rings-source')) {
            this.map.removeSource('fe-airport-range-rings-source');
        }

        this.map = null;
    };

    public applyNavDataFilter = (
        view: UDViewStaticDataModel,
        filterGroups: Record<FilterGroupType, FilterGroup[]>,
        myAirports: string[]
    ) => {
        this.filterGroups = filterGroups;
        if (!this.map) {
            return;
        }

        this.navDataFilter.setFilterGroups(filterGroups);
        this.navDataFilter.setMyAirports(myAirports);
        this.navDataFilter.setData(view);
        this.navDataFilter.applyFilter();
    };

    public updateTerrainShading(settings: UDViewTerrainShadingModel) {
        this.navDataFilter.setTerrainShading({
            ...settings,
            type: settings.type === UDViewTerrainShadingType.singleColor ? 'single' : 'ramp'
        });
    }

    public updateRangeRings = (map: FeMap, settings: UDViewAirportSettingsModel) => {
        if (!settings || !map || map.getZoom() < RANGE_RINGS_MIN_ZOOM) {
            return;
        }
        if (!settings.rangeRingsEnabled) {
            if (map.getSource('fe-airport-range-rings-source')) {
                (map.getSource('fe-airport-range-rings-source') as GeoJSONSource).setData({
                    type: 'FeatureCollection',
                    features: []
                });
            }
            return;
        }

        // Rings can at most show up for only airports displayed on the map. Use the base airports filter and apply additional range ring filtering
        const airportFilter = this.navDataFilter.getAirportFilter();
        const ringFilterGroup = this.filterGroups[FilterGroupType.airport]?.find((x) => x.id === settings.selectedGroupId);
        let ringsFilter: ExpressionFilterSpecification = false;
        if (settings.applyToAllAirports) {
            ringsFilter = true;
        } else {
            if (ringFilterGroup) {
                ringsFilter = [
                    'any',
                    ['in', ['get', 'icaoid'], ['literal', ringFilterGroup.members.map((x) => x.text)]],
                    ['in', ['get', 'iataid'], ['literal', ringFilterGroup.members.map((x) => x.text)]],
                    ['in', ['get', 'faaid'], ['literal', ringFilterGroup.members.map((x) => x.text)]]
                ];
            }
        }

        // this can return thousands of airports depending on airport layer configuration. If we try to render geojson with 5k airports we'll likely crash
        let filteredAirports = map.querySourceFeatures('festatic', {
            sourceLayer: 'airport',
            filter: ['all', airportFilter, ringsFilter]
        });

        if (filteredAirports.length > 500) {
            filteredAirports = filteredAirports.slice(0, 500);
            store.dispatch(
                setNavDataWarning({ workspaceId: this.workspaceId, message: 'Too many airport rings. Limiting display to 500.' })
            );
        } else {
            store.dispatch(setNavDataWarning({ workspaceId: this.workspaceId, message: null }));
        }

        const rangeRingsGeoJSON = this.createRangeRingsGeoJSON(filteredAirports, settings);

        if (map.getSource('fe-airport-range-rings-source')) {
            (map.getSource('fe-airport-range-rings-source') as GeoJSONSource).setData(rangeRingsGeoJSON);
        }
    };

    public updateRunwayDepiction = (settings: UDViewAirportSettingsModel) => {
        this.runways.onIdle(settings);
    };

    public setRunwaySettings = (settings: UDViewAirportSettingsModel) => {
        this.runways.changeSettings(settings);
    };

    private createCircleFeature(lon: number, lat: number, radius: number, props: GeoJsonProperties): Feature {
        const circle = turf.circle([lon, lat], radius, { steps: 64, units: UnitFormatter.distanceUnitTurf });
        return {
            type: 'Feature',
            geometry: circle.geometry,
            properties: props
        };
    }

    private createRangeRingsGeoJSON(airports: GeoJSONFeature[], settings: UDViewAirportSettingsModel): FeatureCollection {
        const features: Feature[] = [];

        // TODO: cache rings based on radius and location, no need to generate new geojson every map move
        airports.forEach((airport) => {
            const position = (airport.geometry as Point).coordinates;
            const longitude = position[0];
            const latitude = position[1];
            // Clone into a plain object: MapLibre's queryRenderedFeatures can return properties
            // with a null prototype, which breaks worker serialization (_classRegistryKey lookup).
            const props = { ...(airport.properties || {}) };

            if (longitude != null && latitude != null) {
                if (settings?.rangeRingsSingleRing) {
                    const radius = settings.rangeRingsSingleRingDistance;
                    features.push(this.createCircleFeature(longitude, latitude, radius, props));
                } else {
                    const count = settings?.rangeRingsMultipleRings || 0;
                    const spacing = settings?.rangeRingsMultipleRingSpacing || 0;

                    for (let i = 1; i <= count; i++) {
                        features.push(this.createCircleFeature(longitude, latitude, i * spacing, props));
                    }
                }
            }
        });

        return {
            type: 'FeatureCollection',
            features
        };
    }

    queryHoverInfo(event: MapMouseEvent) {
        const navData = this.map!.queryRenderedFeatures(event.point, {
            layers: HOVERABLE_LAYERS
        });
        if (navData.length === 0) {
            return null;
        }
        return navData.map((feature) => {
            const properties = feature.properties as GeoJsonProperties;
            const layerName = feature.layer.id;
            return {
                layerName: layerName,
                properties: properties
            };
        });
    }

    queryContextMenuInfo(event: MapMouseEvent, map: MaplibreMap): NavDataContextMenuContent[] | null {
        //add a small bounding box around the point to make it easier to click on small features
        const boundingBox: [PointLike, PointLike] = [
            [event.point.x - 5, event.point.y - 5],
            [event.point.x + 5, event.point.y + 5]
        ];
        const navData = map.queryRenderedFeatures(boundingBox, {
            layers: CONTEXT_MENU_LAYERS
        });

        if (navData.length === 0) {
            return null;
        }

        // Group by layer type and deduplicate by ID
        const airspaceItems = new Map<string, NavDataContextMenuContent>();
        const suaItems = new Map<string, NavDataContextMenuContent>();
        const otherItems: NavDataContextMenuContent[] = [];

        navData.forEach((feature) => {
            const id = feature.properties?.name || feature.properties?.id;
            const properties = feature.properties as GeoJsonProperties;
            const geometry = feature.geometry;
            const layerName = feature.layer.id;

            const mapped: NavDataContextMenuContent = {
                layerName: layerName,
                properties: properties,
                geometry: geometry
            };

            if (!id) {
                otherItems.push(mapped);
                return;
            }

            if (feature.layer.id.startsWith('airspace_')) {
                if (!airspaceItems.has(id)) {
                    const sourceFeatures = map.querySourceFeatures(feature.source, {
                        sourceLayer: feature.sourceLayer,
                        filter: ['==', ['get', 'name'], feature.properties?.name]
                    });

                    const polygonFeatures = sourceFeatures
                        .filter((sf) => sf.geometry.type === 'Polygon' || sf.geometry.type === 'MultiPolygon')
                        .map((sf) => turf.feature(sf.geometry as Polygon | MultiPolygon));

                    if (polygonFeatures.length === 1) {
                        mapped.geometry = polygonFeatures[0].geometry;
                    } else if (polygonFeatures.length > 1) {
                        const unionResult = turf.union(turf.featureCollection(polygonFeatures));
                        mapped.geometry = unionResult?.geometry || geometry;
                    }
                    airspaceItems.set(id, mapped);
                }
            } else if (feature.layer.id.startsWith('sua_')) {
                if (!suaItems.has(id)) {
                    const sourceFeatures = map.querySourceFeatures(feature.source, {
                        sourceLayer: feature.sourceLayer,
                        filter: ['==', ['get', 'name'], feature.properties?.name]
                    });

                    const polygonFeatures = sourceFeatures
                        .filter((sf) => sf.geometry.type === 'Polygon' || sf.geometry.type === 'MultiPolygon')
                        .map((sf) => turf.feature(sf.geometry as Polygon | MultiPolygon));

                    if (polygonFeatures.length === 1) {
                        mapped.geometry = polygonFeatures[0].geometry;
                    } else if (polygonFeatures.length > 1) {
                        const unionResult = turf.union(turf.featureCollection(polygonFeatures));
                        mapped.geometry = unionResult?.geometry || geometry;
                    }
                    suaItems.set(id, mapped);
                }
            } else {
                otherItems.push(mapped);
            }
        });

        return [...airspaceItems.values(), ...suaItems.values(), ...otherItems];
    }
}
